import CrossroadsLogo from '@/components/Work/logos/CrossroadsLogo';
import heroVideo from '@/assets/cases/crossroads/0a.mov';
import primaryHeroVideo from '@/assets/cases/crossroads/00.mov';
import primaryHeroBlur from '@/assets/cases/crossroads/00.blur.webp';
import brandLogo from '@/assets/cases/crossroads/1.jpg';
import brandSystem from '@/assets/cases/crossroads/2a.mov';
import brandLandingHero from '@/assets/cases/crossroads/2b.jpg';
import brandLogin from '@/assets/cases/crossroads/3.jpg';
import featureTerminals from '@/assets/cases/crossroads/4.mov';
import tenantMonitoringDashboard from '@/assets/cases/crossroads/6.png';
import tenantLogFlow from '@/assets/cases/crossroads/7.mov';
import holisticMonitoringVideo from '@/assets/cases/crossroads/8.mov';
import {CaseStudy} from './types';

const crossroads: CaseStudy = {
  // ============================================
  // FIXED METADATA
  // ============================================
  id: 'crossroads-v1',
  slug: 'crossroads-data-integration-platform',
  title: 'Crossroads',
  client: 'Energy Company',
  industry: 'Energy & Utilities',
  projectType: 'Zero-to-One Platform Development + Branding',
  timeline: '6 weeks',
  technologies: ['React', 'TypeScript', 'Ant Design', 'OAuth 2.0', 'RESTful APIs'],

  shortDescription: 'Fortune 100/500 clients in separate databases. Built unified platform—strategy, brand, multi-tenant V1—in 6 weeks.',
    excerpt: 'Enterprise SaaS company dominated refined products but each client environment lived in separate databases. We gave them velocity and vision to make a unified platform real.',
  heroImageUrl: '/images/case-studies/crossroads/hero.jpg',
  logo: CrossroadsLogo,
  featured: true,
  workHighlight: {
    id: 'data-platform-play',
    mediaType: 'video',
    media: primaryHeroVideo,
    mediaSources: [{ src: primaryHeroVideo, type: 'video/quicktime' }],
    placeholder: primaryHeroBlur,
  },

  // ============================================
  // HERO SECTION DATA
  // ============================================
  heroTestimonial: 'We dominated the market but every client was in a separate database. They gave us the velocity and vision to unify it all. Six weeks later we had a real platform.',
  heroStatement:
    'They dominated enterprise SaaS, but without product and design capacity they couldn’t become a data platform—Crossroads is where we closed that gap.',
  heroMetrics: [
    {label: 'Terminals', value: '500+'},
    {label: 'Features shipped', value: '100+'}
  ],
  heroNarrative:
    'Built Crossroads to unify Fortune 100/500 client environments across separate databases. Multi-tenant CMS for integrations and mappings to master data. Brand, strategy, and V1 in six weeks.\n\nMarket leader serving Fortune 100/500 companies. Every client in a separate database. No unified view. No velocity.\n\nOnsite workshop for product strategy. Built brand, multi-tenant V1, and a CMS for integrations and master data mappings. Six weeks later, one platform managing hundreds of client environments.',
  heroGalleryImage: heroVideo,

  // ============================================
  // SCOPE OF WORK (4 Service Areas)
  // ============================================
  scopeOfWork: [
    {
      icon: 'lightbulb',
      title: 'Product Strategy',
      number: '01',
      description: 'Ran onsite workshop to define product positioning, competitive landscape, and roadmap. Company had never built a data platform—we gave them strategy to enter the market with confidence.'
    },
    {
      icon: 'palette',
      title: 'Brand Design',
      number: '02',
      description: 'Created complete brand identity including logo design, color system, typography, and visual guidelines positioning Crossroads as enterprise-grade infrastructure.'
    },
    {
      icon: 'design_services',
      title: 'UI/UX Design',
      number: '03',
      description: 'Designed desktop-optimized admin interface with backend-driven grids, advanced search, bulk operations, and exception handling for power users managing complex data operations.'
    },
    {
      icon: 'code',
      title: 'Platform Development',
      number: '04',
      description: 'Built multi-tenant V1 as a CMS for integrations and mappings. All data connects to a master data set. 100+ features across mapping, master data, exceptions, monitoring, and auth in 6 weeks.'
    }
  ],

  // ============================================
  // CASE DETAILS (Collapsible Card)
  // ============================================
  caseDetails: {
    task:
      'Market leader serving Fortune 100/500 companies had each client in separate databases. They needed to unify it all into one platform.',
    solution:
      'Ran onsite workshop for product strategy. Built brand identity and multi-tenant V1—a CMS for integrations where all mappings connect to master data. Unified hundreds of client environments.',
    result:
      'Six weeks: brand, multi-tenant V1, 100+ features. Unified management across hundreds of Fortune 100/500 client environments.',
    stack: ['Figma', 'React', 'Ant Design', 'TypeScript', 'OAuth 2.0']
  },

  // ============================================
  // DYNAMIC CONTENT (Visual Storytelling)
  // ============================================
  content: [
    // BRAND IDENTITY SECTION
    {
      type: 'heading2',
      content: 'Brand Identity',
      id: 'brand',
      description: 'Enterprise data orchestration visual system'
    },
    {
      type: 'paragraph',
      content: 'The client had many other SaaS products but wanted Crossroads separated from their existing solutions—a distinct platform play. We created a brand that stands apart: enterprise reliability, technical sophistication, centralized control. Crossroads is the hub where hundreds of fragmented client environments connect.'
    },
    // Brand Gallery (1-2-1 cadence)
    {
      type: 'gallery',
      content: {
        rows: [
          {
            layout: 'single',
            images: [
              {
                src: brandLogo,
                alt: 'Crossroads final logo design',
                caption: 'Final logo after three rounds',
              },
            ],
          },
          {
            layout: 'double',
            images: [
              {
                src: brandSystem,
                alt: 'Landing page scroll demonstration',
                caption: 'Landing page scroll interaction',
                kind: 'video',
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
              },
              {
                src: brandLandingHero,
                alt: 'Brand guidelines deck',
                caption: 'Complete brand guidelines deck',
              },
            ],
          },
          {
            layout: 'single',
            images: [
              {
                src: brandLogin,
                alt: 'Crossroads login screen',
                caption: 'Platform login interface',
              },
            ],
          },
        ],
      },
    },

    // PLATFORM CAPABILITIES SECTION
    {
      type: 'heading2',
      content: 'The Platform',
      id: 'platform',
      description: 'Integration CMS with master data foundation'
    },
    {
      type: 'paragraph',
      content: 'Thirty production environments. Disjointed terminals, products, gas stations, companies. Transactional data integration previously cost consulting firms hundreds of thousands of dollars per company per system. Crossroads is an integration CMS that maps all ecosystem data to a master set—terminals, products, sites, tanks, counterparties. Internal users get dynamic, backend-driven screens that guide them through every mapping workflow while the system auto-builds grids and CRUD for each entity. Operators onboard customers, create mappings, monitor integration health. No engineering required. The roadmap is simple now: add entities, add integration support. We built the foundation and a monitoring suite to manage it all.'
    },
    {
      type: 'comparisonTable',
      content: {
        headers: ['Before Crossroads', 'After Crossroads'],
        rows: [
          ['30+ production environments with fragmented data across terminals, products, sites, and counterparties', 'Unified master data set with automated mappings across all entities'],
          ['Consulting firms charging hundreds of thousands per company per system integration', 'Self-service CMS—ops teams onboard customers and manage integrations without engineering'],
          ['No visibility into integration health or failures across environments', 'Real-time monitoring suite with status dashboards, alerting, and exception handling'],
        ],
      },
    },

    {
      type: 'gallery',
      content: {
        heading: 'Master Data Management',
        caption: 'Dynamic screens auto-build grids and CRUD for 5 entity types (Terminals, Products, Sites, Tanks, Counterparties) with bulk import, advanced search, and backend-driven configuration.',
        rows: [
          {
            layout: 'single',
            images: [
              {
                src: featureTerminals,
                alt: 'Terminals master data management',
                caption: 'Terminal entity management with advanced search and filtering',
                kind: 'video',
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
              },
            ],
          },
        ],
      },
    },
    {
      type: 'gallery',
      content: {
        heading: 'Tenant Monitoring',
        caption: 'Tenant owners monitor their own environments, watch real-time job status, and drill into actionable logs to fix mappings fast.',
        rows: [
          {
            layout: 'single',
            images: [
              {
                src: tenantMonitoringDashboard,
                alt: 'Tenant monitoring dashboard with key health indicators',
                caption: 'Dashboard view scoped to a single tenant with integration health, mapping completion, and recent alerts.',
              },
            ],
          },
          {
            layout: 'single',
            images: [
              {
                src: tenantLogFlow,
                alt: 'Tenant integration log flow to offending mapping',
                caption: 'Integration log flow that jumps straight to the offending mapping so tenant admins can fix breaks instantly.',
                kind: 'video',
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
              },
            ],
          },
        ],
      },
    },
    {
      type: 'gallery',
      content: {
        heading: 'Holistic Monitoring',
        caption: 'Central command center spanning every integration and tenant with alerting, throughput metrics, and exception triage.',
        rows: [
          {
            layout: 'single',
            images: [
              {
                src: holisticMonitoringVideo,
                alt: 'Holistic monitoring view across all integrations and tenants',
                caption: 'Ops leaders track cross-tenant intake, throughput, and alert queues from a single, holistic monitoring screen.',
                kind: 'video',
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
              },
            ],
          },
        ],
      },
    },
    // TECHNICAL FEATS SECTION
    {
      type: 'heading2',
      content: 'Technical Feats',
      id: 'technical',
      description: 'Multi-tenant architecture with backend-driven CMS'
    },
    {
      type: 'paragraph',
      content: 'Crossroads functions as a CMS for integrations. Multi-tenant architecture with master data at the core. All mappings flow through centralized entities, so the platform scales to hundreds of clients without frontend changes. Add new integrations, entities, or fields server-side—the UI updates automatically.'
    },

    {
      type: 'keyBulletList',
      content: {
        heading: 'Multi-Tenant Master Reference Architecture',
        description: 'Problem: Client needed ironclad tenant isolation without slowing their internal ops teams.',
        points: [
          {
            icon: 'shield',
            title: 'Tenant-Scoped Isolation',
            text: 'Row-level security + per-tenant secrets meant every energy customer operated in a sealed lane with no cross-contamination.'
          },
          {
            icon: 'hub',
            title: 'Master Reference Data',
            text: 'All mapping flows through centralized master data—different objects, different databases, unified view. Ops can map across any entity type without engineering.'
          },
          {
            icon: 'visibility',
            title: 'Cross-Tenant Operations',
            text: 'Internal teams can hop between tenants to troubleshoot without breaking context or auth, maintaining role-based permissions per tenant.'
          }
        ]
      }
    },

    {
      type: 'keyBulletList',
      content: {
        heading: 'Backend-Driven Integration CMS',
        description: 'Problem: Ops wanted to add integrations and entities weekly without waiting on frontend deployments.',
        points: [
          {
            icon: 'settings_suggest',
            title: 'Auto-Building Grids',
            text: 'Backend owns the entity schema and grid configuration. New entities or fields ship server-side, so the UI updates with zero frontend work.'
          },
          {
            icon: 'add_circle',
            title: 'Self-Service Integration Management',
            text: 'Client can now add integrations and objects as they please. No engineering bottleneck—ops teams configure new data sources directly.'
          },
          {
            icon: 'category',
            title: 'Universal Object Model',
            text: 'Flexible schema supports any entity type (Terminals, Products, Sites, Tanks, Counterparties) with complex relationship handling and cascade operations.'
          }
        ]
      }
    }
  ]
};

export default crossroads;
